import { Injectable, inject } from '@angular/core';
import { AbstractControl, FormGroup } from '@angular/forms';
import { AssetProperty } from '@api/model/models';
import { isValidJSON } from 'src/app/shared/extensions/string.extensions';
import { PermissionsService } from 'src/app/shared/services/permissions/permissions.service';
import { ConfirmationModalService } from '../../confirmation-modal/confirmation-modal.service';
import { DataType, IExtendedControl } from '../models/IExtendedControl';
import { PropertiesComponent } from '../properties.component';
import { isNumeric, setFormValue } from './properties-form.extensions';
import { PropertiesGeneratorBase } from './properties-generator.base';

@Injectable()
export class PropertiesGeneratorService extends PropertiesGeneratorBase {
    private permissionsService = inject(PermissionsService);
    private confirmationModalService = inject(ConfirmationModalService);

    private propertiesComponent: PropertiesComponent;

    public get hasEditPermission(): boolean {
        return this.permissionsService.hasPermissions(['asset.write']);
    }

    public initialize(propertiesComponent: PropertiesComponent) {
        this.propertiesComponent = propertiesComponent;
    }

    public generateFormControls(hasNewProperties: boolean) {
        if (hasNewProperties) {
            this.propertiesComponent.form = new FormGroup({});
        }

        if (this.propertiesComponent.properties) {
            this.extractElements(this.propertiesComponent.form, this.propertiesComponent.properties, null, this.hasEditPermission);
        }

        if (this.propertiesComponent.propertyValues) {
            if (this.propertiesComponent.controls.length > 0) {
                // Fill controls with values
                const complexDesiredValues = this.getComplexValues('desiredValue') as { [key: string]: object | string };
                const complexReportedValues = this.getComplexValues('reportedValue') as { [key: string]: object | string };
                const values = this.propertiesComponent.propertyValues.reduce(
                    (obj, value) => ({
                        ...obj,
                        [value.name]: value.desiredValue ?? value.reportedValue,
                    }),
                    {}
                );

                // Merge reported values to desired values
                this.mergeComplexValues(complexReportedValues as { [key: string]: object }, complexDesiredValues as { [key: string]: object });

                const formValues = { ...values, ...complexDesiredValues };
                for (const key of Object.keys(formValues)) {
                    const control = this.propertiesComponent.form.controls[key] as unknown as IExtendedControl;
                    if (control == null) {
                        continue;
                    }

                    setFormValue(control, formValues, key, this.propertiesComponent);
                }

                this.propertiesComponent.form.patchValue(formValues);
                // Check values, whether DesiredValue and ReportedValue are different
                for (const ctrl of this.propertiesComponent.controls) {
                    const control = ctrl as unknown as IExtendedControl;
                    this.checkDifferentValues(control, complexDesiredValues as { [key: string]: string }, complexReportedValues as { [key: string]: string }, values);
                }

                this.attachMetadataToControl();
            }
        }
    }

    public getComplexPropertyValues(): object {
        const complexProperties = this.getComplexProperties();

        const complexPropertyValues: { [key: string]: string } = {};
        let values: { [key: string]: string | number } = null;
        let nameOfParent = '';
        for (const control of complexProperties) {
            const ctrl = control as IExtendedControl;
            if (nameOfParent !== ctrl.nameOfParent) {
                nameOfParent = ctrl.nameOfParent;
                values = {};
            }

            let controlName = ctrl.name;
            // Build value for ArrayT: { IndexOfChangedPorperty: value } => { 1: 'value1' }
            if (ctrl.parentSchemaType == DataType.Array) {
                const lastIndex = controlName.lastIndexOf('_') + 1;
                controlName = controlName.substring(lastIndex, controlName.length);
            }

            if (ctrl.hasGradientOrOffset) {
                values[controlName] = ctrl.value / ctrl.gradient - ctrl.offset;
            } else {
                values[controlName] = ctrl.value;
            }

            complexPropertyValues[nameOfParent] = JSON.stringify(values);
        }
        return complexPropertyValues;
    }

    public async allowChangeProperty(control: IExtendedControl, isIOLinkDevice: boolean): Promise<boolean> {
        if (control.needsConfirmation) {
            let confirmationMessage = this.translationService.translate('properties.confirmationDialog.needsConfirmation');
            if (isIOLinkDevice) {
                confirmationMessage = this.translationService.translate('properties.confirmationDialog.isIOLinkDevice');
            }
            const confirm = await this.confirmationModalService.openDefaultConfirmation(this.translationService.translate('properties.confirmationDialog.needsConfirmationTitle'), confirmationMessage);

            return confirm;
        }

        return true;
    }

    private attachMetadataToControl() {
        for (const property of this.propertiesComponent.propertyValues) {
            // Either match the name directly or match the parent name for complex properties
            var ctrls = this.propertiesComponent.controls.map((ctrl) => ctrl as IExtendedControl).filter((ctrl) => ctrl.name == property.name || ctrl.nameOfParent == property.name);

            for (var ctrl of ctrls) {
                ctrl.propertyValueMetadata = property;
            }
        }
    }

    private getComplexValues(type: keyof IExtendedControl | keyof AssetProperty): Object {
        const complexValues = {};

        this.propertiesComponent.propertyValues
            .filter((entity) => isValidJSON(entity[type as keyof AssetProperty] ?? entity.reportedValue))
            .map((entity) => ({ ...entity, [type]: JSON.parse(entity[type as keyof AssetProperty] ?? entity.reportedValue) }))
            .forEach((entity) => {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const obj = { ...(entity as any)[type] };
                const keys = Object.keys(obj);

                for (const key of keys) {
                    // check key, wheather it is an array type (isNumber)
                    // ArrayT format: { IndexOfPorperty: value } => { 1: 'value1', 2: 'value2' }
                    const isNumber = !Number.isNaN(Number(key));
                    let value = null;
                    if (isNumber) {
                        value = { [`${entity.name}_${key}`]: obj[key] };
                    } else {
                        value = { [key]: obj[key] };
                    }
                    Object.assign(complexValues, value);
                }
            });

        return complexValues;
    }

    private getComplexProperties(): AbstractControl[] {
        const parentOfProperties: string[] = [];
        Object.values(this.propertiesComponent.form.controls)
            .filter((control) => (control as IExtendedControl).nameOfParent?.length && control.dirty)
            .forEach((control) => {
                const ctrl = control as IExtendedControl;
                if (parentOfProperties.indexOf(ctrl.nameOfParent) === -1) parentOfProperties.push(ctrl.nameOfParent);
            });

        const complexProperties = Object.values(this.propertiesComponent.form.controls).filter((control) => parentOfProperties.indexOf((control as IExtendedControl).nameOfParent) > -1 && control.dirty);
        return complexProperties;
    }

    // check if there are difference between reported value and desired value
    private checkDifferentValues(control: IExtendedControl, complexDesiredValues: { [key: string]: string }, complexReportedValues: { [key: string]: string }, values: AssetProperty): void {
        const desiredValue = this.propertiesComponent.propertyValues?.find((entity) => entity.name === control.name)?.desiredValue;

        if (!this.propertiesComponent.originalValues.has(control.name)) {
            this.propertiesComponent.originalValues.set(control.name, desiredValue !== undefined ? desiredValue : control.value);
        }

        control.markAsTouched();
        const isNumber = isNumeric(control.dataType);

        let desired: unknown;
        let reported: unknown;

        if (control.nameOfParent?.length) {
            desired = complexDesiredValues[control.name];
            reported = complexReportedValues[control.name];
        } else {
            desired = values[control.name as keyof AssetProperty];
            reported = this.propertiesComponent.propertyValues?.find((x) => x.name === control.name)?.reportedValue;
        }

        control.outOfSync = this.areValuesDifferent(desired, reported, isNumber);
        control.reportedValue = reported as string;

        if (isNumber) {
            control.reportedValue = String(Number(control.reportedValue) * control.gradient + control.offset);
        }
    }

    private mergeComplexValues(complexReportedValues: { [key: string]: object }, complexDesiredValues: { [key: string]: object }) {
        for (const key of Object.keys(complexReportedValues)) {
            if (complexDesiredValues[key] == null) {
                Object.assign(complexDesiredValues, { [key]: complexReportedValues[key] });
            }
        }
    }
}
